import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, isSameMonth,
  isToday, getHours, getMinutes, parseISO, setHours, setMinutes,
  startOfDay, addHours, differenceInMinutes, eachDayOfInterval
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronLeft, ChevronRight, Plus, MapPin, Phone, User, Clock,
  Building2, Calendar as CalendarIcon, Eye, EyeOff, X, Zap, Search, Maximize2, Minimize2, Navigation, Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

// === CONSTANTES ===
const HOUR_START = 7;
const HOUR_END = 21;
const HOUR_HEIGHT = 40; // px par heure — compact pour vue d'ensemble
const TOTAL_HOURS = HOUR_END - HOUR_START;

const STATUS_COLORS = {
  planifie: { bg: 'bg-gray-400', border: 'border-gray-500', light: 'bg-gray-50', text: 'text-gray-700', dot: 'bg-gray-400' },
  confirme: { bg: 'bg-cyan-500', border: 'border-cyan-600', light: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
  realise: { bg: 'bg-blue-500', border: 'border-blue-600', light: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  reporte: { bg: 'bg-yellow-400', border: 'border-yellow-500', light: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-400' },
  annule: { bg: 'bg-red-500', border: 'border-red-600', light: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
};

const RESULTAT_COLORS = {
  signe: { bg: 'bg-green-500', border: 'border-green-600', light: 'bg-green-50', text: 'text-green-700' },
  non_signe: { bg: 'bg-gray-400', border: 'border-gray-500', light: 'bg-gray-50', text: 'text-gray-600' },
  r2: { bg: 'bg-orange-500', border: 'border-orange-600', light: 'bg-orange-50', text: 'text-orange-700' },
  r2_devis: { bg: 'bg-orange-400', border: 'border-orange-500', light: 'bg-orange-50', text: 'text-orange-600' },
  r2_reflexion: { bg: 'bg-orange-400', border: 'border-orange-500', light: 'bg-orange-50', text: 'text-orange-600' },
};

const STATUS_LABELS = {
  planifie: 'Planifié', confirme: 'Confirmé', realise: 'Réalisé', reporte: 'Reporté', annule: 'Annulé'
};
const RESULTAT_LABELS = {
  signe: 'Signé', r2: 'R2', r2_devis: 'R2 Devis', r2_reflexion: 'R2 Réflexion', non_signe: 'Non signé', hors_critere: 'Hors critère', pas_interesse: 'Pas intéressé'
};
const TYPE_RDV_LABELS = {
  visite_commerciale: 'Visite commerciale', appel_telephonique: 'Appel téléphonique', reunion: 'Réunion'
};

function getEventColor(rdv) {
  if (rdv.resultat && RESULTAT_COLORS[rdv.resultat]) return RESULTAT_COLORS[rdv.resultat];
  if (rdv.confirmed && rdv.status === 'planifie') return STATUS_COLORS.confirme;
  return STATUS_COLORS[rdv.status] || STATUS_COLORS.planifie;
}

// === COMPOSANT PRINCIPAL ===
const ProspectCalendar = ({ onSlotClick, onRefreshNeeded, onEventClick, onEditProspect, onEditRdv, onDeleteRdv, renderOptimBar, userFilter, commercialFilter }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState(window.innerWidth < 768 ? '3days' : 'week');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [indisponibilites, setIndisponibilites] = useState([]);
  const [showIndispoDialog, setShowIndispoDialog] = useState(false);
  const [indispoForm, setIndispoForm] = useState({ date_debut: '', date_fin: '', motif: '', user_id: '' });
  const [statusFilter, setStatusFilter] = useState(null);
  const [optimAddress, setOptimAddress] = useState('');
  const [optimSuggestions, setOptimSuggestions] = useState([]);
  const [optimCoords, setOptimCoords] = useState(null); // null = tout afficher
  const scrollRef = useRef(null);
  const calendarRef = useRef(null);

  // Détecter le resize pour basculer mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(userData);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (currentUser) fetchCalendarData();
  }, [currentDate, viewMode, currentUser]);

  const getDateRange = useCallback(() => {
    if (viewMode === 'day') {
      return { start: startOfDay(currentDate), end: addDays(startOfDay(currentDate), 1) };
    } else if (viewMode === '3days') {
      return { start: startOfDay(currentDate), end: addDays(startOfDay(currentDate), 3) };
    } else if (viewMode === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      return { start: ws, end: addDays(ws, 7) };
    } else {
      const ms = startOfMonth(currentDate);
      const me = endOfMonth(currentDate);
      return { start: startOfWeek(ms, { weekStartsOn: 1 }), end: addDays(endOfWeek(me, { weekStartsOn: 1 }), 1) };
    }
  }, [currentDate, viewMode]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { start, end } = getDateRange();
      const res = await axios.get(`${API}/api/rendez-vous/calendar`, {
        params: { start: start.toISOString(), end: end.toISOString() },
        headers: { Authorization: `Bearer ${token}` }
      });
      // Nouvelle structure: { rdvs: [...], indisponibilites: [...] }
      if (res.data.rdvs) {
        setEvents(res.data.rdvs);
        setIndisponibilites(res.data.indisponibilites || []);
      } else {
        // Fallback si ancienne structure (array directe)
        setEvents(Array.isArray(res.data) ? res.data : []);
        setIndisponibilites([]);
      }
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Erreur chargement calendrier');
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to 8h on mount
  useEffect(() => {
    if (scrollRef.current && viewMode !== 'month') {
      const scrollTo = (8 - HOUR_START) * HOUR_HEIGHT;
      scrollRef.current.scrollTop = scrollTo;
    }
  }, [viewMode, events]);

  // Refresh quand le parent le demande (après création d'un prospect+RDV)
  useEffect(() => {
    if (onRefreshNeeded) fetchCalendarData();
  }, [onRefreshNeeded]);

  // === NAVIGATION ===
  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, -1));
    else if (viewMode === '3days') setCurrentDate(d => addDays(d, -3));
    else if (viewMode === 'week') setCurrentDate(d => subWeeks(d, 1));
    else setCurrentDate(d => subMonths(d, 1));
  };
  const goNext = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, 1));
    else if (viewMode === '3days') setCurrentDate(d => addDays(d, 3));
    else if (viewMode === 'week') setCurrentDate(d => addWeeks(d, 1));
    else setCurrentDate(d => addMonths(d, 1));
  };

  const getHeaderTitle = () => {
    if (viewMode === 'day') return format(currentDate, isMobile ? 'EEE d MMM' : 'EEEE d MMMM yyyy', { locale: fr });
    if (viewMode === '3days') {
      const end = addDays(currentDate, 2);
      return `${format(currentDate, 'd', { locale: fr })} – ${format(end, 'd MMM', { locale: fr })}`;
    }
    if (viewMode === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = addDays(ws, 6);
      if (ws.getMonth() === we.getMonth()) return format(ws, 'MMMM yyyy', { locale: fr });
      return `${format(ws, 'MMM', { locale: fr })} – ${format(we, 'MMM yyyy', { locale: fr })}`;
    }
    return format(currentDate, 'MMMM yyyy', { locale: fr });
  };

  // === GESTION CLICK CRÉNEAU ===
  const handleSlotClick = (date, hour, minutes = 0) => {
    const dt = setMinutes(setHours(new Date(date), hour), minutes);
    const formattedDate = format(dt, "yyyy-MM-dd'T'HH:mm");
    // Déléguer au parent qui ouvre le formulaire complet avec date pré-remplie
    if (onSlotClick) {
      onSlotClick(formattedDate);
    }
  };

  // === CURRENT TIME LINE ===
  const CurrentTimeLine = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
      const timer = setInterval(() => setNow(new Date()), 60000);
      return () => clearInterval(timer);
    }, []);
    const hours = getHours(now);
    const mins = getMinutes(now);
    if (hours < HOUR_START || hours >= HOUR_END) return null;
    const top = ((hours - HOUR_START) + mins / 60) * HOUR_HEIGHT;
    return (
      <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: `${top}px` }}>
        <div className="flex items-center">
          <div className="w-2.5 h-2.5 bg-red-500 rounded-full -ml-1.5 shrink-0" />
          <div className="flex-1 h-[2px] bg-red-500" />
        </div>
      </div>
    );
  };

  // === CALCUL DES CHEVAUCHEMENTS ===
  const computeOverlapLayout = (dayEvents) => {
    if (!dayEvents.length) return [];
    // Trier par heure de début
    const sorted = dayEvents.map(ev => {
      const d = typeof ev.date_rdv === 'string' ? parseISO(ev.date_rdv) : new Date(ev.date_rdv);
      const startMin = getHours(d) * 60 + getMinutes(d);
      const endMin = startMin + (ev.duree_minutes || 60);
      return { ...ev, _start: startMin, _end: endMin };
    }).sort((a, b) => a._start - b._start);

    // Attribuer colonne à chaque événement
    const result = [];
    const columns = []; // columns[i] = heure de fin du dernier event dans la colonne i
    for (const ev of sorted) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        if (ev._start >= columns[c]) {
          columns[c] = ev._end;
          result.push({ event: ev, colIndex: c });
          placed = true;
          break;
        }
      }
      if (!placed) {
        result.push({ event: ev, colIndex: columns.length });
        columns.push(ev._end);
      }
    }
    const totalCols = columns.length;
    return result.map(r => ({ ...r, colTotal: totalCols }));
  };


  // === OPTIMISATION CRÉNEAUX ===
  const searchOptimAddress = async (query) => {
    if (query.trim().length < 3) { setOptimSuggestions([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
      const data = await res.json();
      setOptimSuggestions(data.features || []);
    } catch (e) { setOptimSuggestions([]); }
  };

  const openCreneauxOptimises = () => {
    if (!optimCoords) { toast.error("Sélectionnez une adresse d'abord"); return; }
    const params = new URLSearchParams({
      address: optimAddress,
      lat: optimCoords.lat,
      lng: optimCoords.lng,
      cp: optimCoords.cp || '',
      ville: optimCoords.ville || ''
    });
    window.open(`/creneaux-optimises?${params.toString()}`, '_blank');
  };


  // === EXPORT XLSX + LISTING des RDV reportés ===
  const canExport = currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'administratif';
  const canViewReportes = canExport || currentUser?.role === 'superviseur';
  const [showReportesListing, setShowReportesListing] = useState(false);

  const exportReportesXLSX = () => {
    const reportes = events.filter(ev => ev.status === 'reporte' && ev.is_own && ev.prospect);
    if (reportes.length === 0) { toast.error('Aucun RDV reporté à exporter'); return; }

    const data = reportes.map(ev => {
      const p = ev.prospect || {};
      const d = ev.date_rdv ? new Date(ev.date_rdv) : null;
      return {
        'Date RDV': d ? d.toLocaleDateString('fr-FR') : '',
        'Heure': d ? `${d.getHours()}h${String(d.getMinutes()).padStart(2,'0')}` : '',
        'Nom': p.nom || '',
        'Prénom': p.prenom || '',
        'Téléphone': p.telephone || '',
        'Email': p.email || '',
        'Adresse': p.adresse || '',
        'Code Postal': p.code_postal || '',
        'Ville': p.ville || '',
        'Type Énergie': p.type_energie || '',
        'Fournisseur': p.fournisseur_actuel || '',
        'Conso €/an': p.consommation_annuelle_euro || '',
        'Conso kWh/an': p.consommation_annuelle_kwh || '',
        'Mode Chauffage': p.mode_chauffage_actuel || '',
        'Type Toiture': p.type_toiture || '',
        'Orientation Toit': p.orientation_toit || '',
        'Surface Toit m²': p.nombre_m2_toit || '',
        'Velux/Chien Assis': p.velux_chien_assis || '',
        'Zone Classée': p.zone_classee || '',
        'Nb Enfants': p.nombre_enfants || '',
        'Âge M.': p.age_monsieur || '',
        'Âge Mme': p.age_madame || '',
        'Situation Familiale': p.situation_familiale || '',
        'Contrat Travail': p.type_contrat_travail || '',
        'Crédit Immobilier': p.montant_credit_immobilier || '',
        'Autre Crédit': p.autre_credit || '',
        'Notes': p.notes || '',
        'Commentaire RDV': ev.commentaire_rdv || '',
        'Créé par': ev.created_by_name || '',
        'Assigné à': ev.assigned_to_name || '',
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RDV Reportés');
    
    // Auto-width columns
    const colWidths = Object.keys(data[0]).map(k => ({ wch: Math.max(k.length, 12) }));
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `RDV_Reportes_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`${reportes.length} RDV reporté(s) exporté(s)`);
  };


  // === INDISPONIBILITÉS ===
  const canManageIndispo = currentUser?.role === 'admin' || currentUser?.role === 'administratif' || currentUser?.role === 'confirmateur' || currentUser?.role === 'commercial' || currentUser?.role === 'regie';
  const canAssignIndispo = currentUser?.role === 'admin' || currentUser?.role === 'administratif' || currentUser?.role === 'confirmateur';

  const handleCreateIndispo = async () => {
    if (!indispoForm.date_debut || !indispoForm.date_fin) { toast.error('Dates obligatoires'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/indisponibilites`, indispoForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Indisponibilité ajoutée');
      setShowIndispoDialog(false);
      setIndispoForm({ date_debut: '', date_fin: '', motif: '', user_id: '' });
      fetchCalendarData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteIndispo = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/indisponibilites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Indisponibilité supprimée');
      fetchCalendarData();
    } catch (err) { toast.error('Erreur'); }
  };


  // Filtrage par statut + par utilisateur
  const getFilteredEvents = () => {
    let result = events;
    // Filtre par utilisateur (télépro)
    if (userFilter) {
      result = result.filter(ev => ev.created_by === userFilter);
    }
    // Filtre par commercial
    if (commercialFilter) {
      result = result.filter(ev => ev.assigned_to === commercialFilter);
    }
    // Filtre par statut
    if (!statusFilter) return result;
    return result.filter(ev => {
      if (statusFilter === 'confirme') return ev.confirmed && ev.status === 'planifie';
      if (statusFilter === 'signe') return ev.resultat === 'signe';
      if (statusFilter === 'r2') return ev.resultat && ev.resultat.startsWith('r2');
      if (statusFilter === 'planifie') return ev.status === 'planifie' && !ev.confirmed;
      return ev.status === statusFilter;
    });
  };
  const filteredEvents = getFilteredEvents();

  const getIndisposForDay = (day) => indisponibilites.filter(ind => {
    try {
      const start = parseISO(ind.date_debut);
      const end = parseISO(ind.date_fin);
      return isSameDay(start, day) || isSameDay(end, day) || (start <= day && end >= day);
    } catch { return false; }
  });

  const IndispoBlock = ({ indispo, dayDate }) => {
    const start = parseISO(indispo.date_debut);
    const end = parseISO(indispo.date_fin);
    const startH = isSameDay(start, dayDate) ? getHours(start) + getMinutes(start) / 60 : HOUR_START;
    const endH = isSameDay(end, dayDate) ? getHours(end) + getMinutes(end) / 60 : HOUR_END;
    if (endH <= HOUR_START || startH >= HOUR_END) return null;
    const topH = Math.max(startH, HOUR_START);
    const botH = Math.min(endH, HOUR_END);
    const top = (topH - HOUR_START) * HOUR_HEIGHT;
    const height = Math.max((botH - topH) * HOUR_HEIGHT, 16);

    return (
      <div
        className="absolute rounded-md border-l-[3px] border-red-400 bg-red-50 cursor-pointer overflow-hidden z-[5] shadow-sm"
        style={{ top: `${top}px`, height: `${height}px`, left: '0', right: '15%', background: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(239,68,68,0.08) 4px, rgba(239,68,68,0.08) 8px)' }}
        onClick={(e) => { e.stopPropagation(); if (window.confirm(`Supprimer l'indisponibilité de ${indispo.user_name || '?'} ?\n${indispo.motif || ''}`)) handleDeleteIndispo(indispo.id); }}
      >
        <div className="bg-red-50/80 h-full flex items-start px-1.5 py-0.5">
          <p className="text-[9px] text-red-500 font-semibold truncate leading-tight">
            {indispo.user_name || 'INDISPO'}{indispo.motif ? ` — ${indispo.motif}` : ''}
          </p>
        </div>
      </div>
    );
  };

  // === Confirmation rapide 1 clic ===
  const canConfirm = currentUser?.role === 'admin' || currentUser?.role === 'administratif' || currentUser?.role === 'confirmateur';

  const handleQuickConfirm = async (e, event) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/rendez-vous/${event.id}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(event.confirmed ? 'Confirmation annulée' : 'RDV confirmé ✅');
      fetchCalendarData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // === EVENT CARD (Week/Day) ===
  const EventBlock = ({ event, colIndex = 0, colTotal = 1 }) => {
    const rdvDate = typeof event.date_rdv === 'string' ? parseISO(event.date_rdv) : new Date(event.date_rdv);
    const hours = getHours(rdvDate);
    const mins = getMinutes(rdvDate);
    if (hours < HOUR_START || hours >= HOUR_END) return null;

    const top = ((hours - HOUR_START) + mins / 60) * HOUR_HEIGHT;
    const duration = event.duree_minutes || 60;
    const height = Math.max((duration / 60) * HOUR_HEIGHT, 24);
    const color = getEventColor(event);
    const prospect = event.prospect;
    const isOwn = event.is_own;

    const marginRight = 15;
    const widthPercent = ((1 / colTotal) * 100) - marginRight / colTotal;
    const leftPercent = (colIndex / colTotal) * 100;

    return (
      <div
        className={`absolute rounded-md px-1 py-0.5 cursor-pointer overflow-hidden border-l-[3px] shadow-sm hover:shadow-md transition-shadow z-10 ${color.border}`}
        style={{ top: `${top}px`, height: `${height}px`, left: `${leftPercent}%`, width: `${widthPercent}%`, backgroundColor: isOwn ? undefined : '#f3f4f6' }}
        onClick={(e) => { e.stopPropagation(); onEventClick ? onEventClick(event) : setSelectedEvent(event); }}
      >
        <div className={`h-full overflow-hidden ${isOwn ? color.light : ''} rounded-sm relative`}>
          {isOwn && prospect ? (
            <>
              <div className="flex items-center gap-0.5">
                {/* Bouton confirmation 1 clic — admin/confirmateur seulement */}
                {canConfirm && event.status === 'planifie' && (
                  <button
                    onClick={(e) => handleQuickConfirm(e, event)}
                    className={`shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold transition-colors
                      ${event.confirmed ? 'bg-cyan-500 text-white' : 'bg-white border border-gray-300 text-gray-400 hover:border-cyan-400 hover:text-cyan-500'}`}
                    title={event.confirmed ? 'Annuler la confirmation' : 'Confirmer le RDV'}
                  >
                    ✓
                  </button>
                )}
                {event.confirmed && !canConfirm && (
                  <span className="shrink-0 w-3.5 h-3.5 rounded-full bg-cyan-500 text-white flex items-center justify-center text-[8px] font-bold">✓</span>
                )}
                <p className="text-[10px] font-semibold truncate leading-tight text-gray-900">
                  {prospect.code_postal && <span className="text-blue-600 font-bold">({prospect.code_postal})</span>} {prospect.nom} {prospect.prenom}
                </p>
              </div>
              {height > 24 && (
                <p className="text-[10px] text-gray-600 truncate leading-tight">
                  {format(rdvDate, 'HH:mm')} – {prospect.ville || ''}
                </p>
              )}
              {height > 35 && (
                event.assigned_to_name ? (
                  <p className="text-[9px] text-red-600 font-semibold truncate leading-tight">
                    {event.assigned_to_name}
                  </p>
                ) : prospect.telephone ? (
                  <p className="text-[9px] text-gray-500 truncate leading-tight">
                    {prospect.telephone}
                  </p>
                ) : null
              )}
            </>
          ) : (
            <>
              <p className="text-[10px] font-medium truncate leading-tight text-gray-500 italic">
                {prospect?.code_postal} {prospect?.ville || 'RDV'}
              </p>
              {height > 24 && (
                <p className="text-[9px] text-gray-400 truncate leading-tight">
                  {format(rdvDate, 'HH:mm')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // === WEEK VIEW ===
  const WeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);

    const getEventsForDay = (day) => filteredEvents.filter(e => {
      const d = typeof e.date_rdv === 'string' ? parseISO(e.date_rdv) : new Date(e.date_rdv);
      return isSameDay(d, day);
    });

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Day headers — compact */}
        <div className="flex border-b border-gray-200 bg-white sticky top-0 z-30">
          <div className="w-12 shrink-0" />
          {days.map((day, i) => (
            <div key={i} className={`flex-1 text-center py-1 border-l border-gray-100 ${isToday(day) ? 'bg-blue-50' : ''}`}>
              <button
                onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors
                  ${isToday(day) ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <span className="uppercase">{format(day, 'EEE', { locale: fr })}</span>
                <span>{format(day, 'd')}</span>
              </button>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="flex" style={{ height: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}>
            {/* Time gutter */}
            <div className="w-12 shrink-0 relative">
              {hours.map(h => (
                <div key={h} className="absolute left-0 right-0 text-right pr-1" style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT - 5}px` }}>
                  <span className="text-[10px] text-gray-400">{String(h).padStart(2, '0')}h</span>
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days.map((day, dayIdx) => {
              const dayEvents = getEventsForDay(day);
              return (
                <div key={dayIdx} className={`flex-1 relative border-l border-gray-100 ${isToday(day) ? 'bg-blue-50/30' : ''}`}>
                  {/* Hour lines */}
                  {hours.map(h => (
                    <div
                      key={h}
                      className="absolute left-0 right-0 border-t border-gray-100 cursor-pointer hover:bg-blue-50/50 transition-colors"
                      style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT / 2}px` }}
                      onClick={() => handleSlotClick(day, h, 0)}
                    />
                  ))}
                  {hours.map(h => (
                    <div
                      key={`${h}-30`}
                      className="absolute left-0 right-0 border-t border-gray-50 border-dashed cursor-pointer hover:bg-blue-50/50 transition-colors"
                      style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, height: `${HOUR_HEIGHT / 2}px` }}
                      onClick={() => handleSlotClick(day, h, 30)}
                    />
                  ))}
                  {/* Events — avec gestion des chevauchements */}
                  {computeOverlapLayout(dayEvents).map(({ event: ev, colIndex, colTotal }) => 
                    <EventBlock key={ev.id} event={ev} colIndex={colIndex} colTotal={colTotal} />
                  )}
                  {/* Indisponibilités */}
                  {getIndisposForDay(day).map(ind => <IndispoBlock key={ind.id} indispo={ind} dayDate={day} />)}
                  {/* Current time line */}
                  {isToday(day) && <CurrentTimeLine />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };


  // === 3 DAYS VIEW (Mobile Google Calendar style) ===
  const ThreeDaysView = () => {
    const days = Array.from({ length: 3 }, (_, i) => addDays(currentDate, i));
    const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);

    const getEventsForDay = (day) => filteredEvents.filter(e => {
      const d = typeof e.date_rdv === 'string' ? parseISO(e.date_rdv) : new Date(e.date_rdv);
      return isSameDay(d, day);
    });

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Day headers — mobile compact */}
        <div className="flex border-b border-gray-200 bg-white sticky top-0 z-30">
          <div className="w-10 shrink-0" />
          {days.map((day, i) => (
            <div key={i} className={`flex-1 text-center py-1.5 border-l border-gray-100 ${isToday(day) ? 'bg-blue-50' : ''}`}>
              <button
                onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                className={`inline-flex flex-col items-center px-2 py-0.5 rounded-lg text-xs font-medium transition-colors
                  ${isToday(day) ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <span className="uppercase text-[10px]">{format(day, 'EEE', { locale: fr })}</span>
                <span className="text-sm">{format(day, 'd')}</span>
              </button>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="flex" style={{ height: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}>
            <div className="w-10 shrink-0 relative">
              {hours.map(h => (
                <div key={h} className="absolute left-0 right-0 text-right pr-1" style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT - 5}px` }}>
                  <span className="text-[9px] text-gray-400">{String(h).padStart(2, '0')}h</span>
                </div>
              ))}
            </div>
            {days.map((day, dayIdx) => {
              const dayEvents = getEventsForDay(day);
              return (
                <div key={dayIdx} className={`flex-1 relative border-l border-gray-100 ${isToday(day) ? 'bg-blue-50/30' : ''}`}>
                  {hours.map(h => (
                    <div key={h} className="absolute left-0 right-0 border-t border-gray-100 cursor-pointer active:bg-blue-100 transition-colors"
                      style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT / 2}px` }}
                      onClick={() => handleSlotClick(day, h, 0)} />
                  ))}
                  {hours.map(h => (
                    <div key={`${h}-30`} className="absolute left-0 right-0 border-t border-gray-50 border-dashed cursor-pointer active:bg-blue-100 transition-colors"
                      style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, height: `${HOUR_HEIGHT / 2}px` }}
                      onClick={() => handleSlotClick(day, h, 30)} />
                  ))}
                  {computeOverlapLayout(dayEvents).map(({ event: ev, colIndex, colTotal }) =>
                    <EventBlock key={ev.id} event={ev} colIndex={colIndex} colTotal={colTotal} />
                  )}
                  {isToday(day) && <CurrentTimeLine />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // === DAY VIEW ===
  const DayView = () => {
    const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);
    const dayEvents = filteredEvents.filter(e => {
      const d = typeof e.date_rdv === 'string' ? parseISO(e.date_rdv) : new Date(e.date_rdv);
      return isSameDay(d, currentDate);
    });

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Day header — compact */}
        <div className="flex border-b border-gray-200 bg-white sticky top-0 z-30">
          <div className="w-12 shrink-0" />
          <div className={`flex-1 text-center py-1 ${isToday(currentDate) ? 'bg-blue-50' : ''}`}>
            <p className={`text-sm font-medium ${isToday(currentDate) ? 'text-blue-600' : 'text-gray-900'}`}>
              {format(currentDate, 'EEEE d MMMM', { locale: fr })}
            </p>
          </div>
        </div>

        {/* Time grid */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <div className="flex" style={{ height: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}>
            <div className="w-12 shrink-0 relative">
              {hours.map(h => (
                <div key={h} className="absolute left-0 right-0 text-right pr-1" style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT - 5}px` }}>
                  <span className="text-[10px] text-gray-400">{String(h).padStart(2, '0')}h</span>
                </div>
              ))}
            </div>
            <div className={`flex-1 relative ${isToday(currentDate) ? 'bg-blue-50/30' : ''}`}>
              {hours.map(h => (
                <div key={h} className="absolute left-0 right-0 border-t border-gray-100 cursor-pointer hover:bg-blue-50/50 transition-colors"
                  style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT / 2}px` }}
                  onClick={() => handleSlotClick(currentDate, h, 0)} />
              ))}
              {hours.map(h => (
                <div key={`${h}-30`} className="absolute left-0 right-0 border-t border-gray-50 border-dashed cursor-pointer hover:bg-blue-50/50 transition-colors"
                  style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, height: `${HOUR_HEIGHT / 2}px` }}
                  onClick={() => handleSlotClick(currentDate, h, 30)} />
              ))}
              {computeOverlapLayout(dayEvents).map(({ event: ev, colIndex, colTotal }) => 
                <EventBlock key={ev.id} event={ev} colIndex={colIndex} colTotal={colTotal} />
              )}
              {getIndisposForDay(currentDate).map(ind => <IndispoBlock key={ind.id} indispo={ind} dayDate={currentDate} />)}
              {isToday(currentDate) && <CurrentTimeLine />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // === MONTH VIEW ===
  const MonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const allDays = eachDayOfInterval({ start: calStart, end: calEnd });
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) weeks.push(allDays.slice(i, i + 7));

    const getEventsForDay = (day) => filteredEvents.filter(e => {
      const d = typeof e.date_rdv === 'string' ? parseISO(e.date_rdv) : new Date(e.date_rdv);
      return isSameDay(d, day);
    });

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {/* Day names header */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-white sticky top-0 z-30">
          {['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'].map(d => (
            <div key={d} className="py-2 text-center text-xs font-medium text-gray-500">{d}</div>
          ))}
        </div>
        {/* Weeks */}
        <div className="flex-1 overflow-y-auto">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-gray-100" style={{ minHeight: '120px' }}>
              {week.map((day, di) => {
                const dayEvs = getEventsForDay(day);
                const inMonth = isSameMonth(day, currentDate);
                return (
                  <div
                    key={di}
                    className={`border-l border-gray-100 p-1 cursor-pointer hover:bg-gray-50 transition-colors min-h-[120px]
                      ${!inMonth ? 'bg-gray-50/50' : ''} ${isToday(day) ? 'bg-blue-50/60' : ''}`}
                    onClick={() => { if (dayEvs.length === 0) handleSlotClick(day, 9, 0); }}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentDate(day); setViewMode('day'); }}
                      className={`w-7 h-7 rounded-full text-sm flex items-center justify-center mb-1
                        ${isToday(day) ? 'bg-blue-600 text-white font-bold' : inMonth ? 'text-gray-900 hover:bg-gray-200' : 'text-gray-400'}`}
                    >
                      {format(day, 'd')}
                    </button>
                    <div className="space-y-0.5">
                      {dayEvs.slice(0, 3).map(ev => {
                        const color = getEventColor(ev);
                        const rdvDate = typeof ev.date_rdv === 'string' ? parseISO(ev.date_rdv) : new Date(ev.date_rdv);
                        return (
                          <div
                            key={ev.id}
                            className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer ${color.light} ${color.text} hover:opacity-80`}
                            onClick={(e) => { e.stopPropagation(); onEventClick ? onEventClick(ev) : setSelectedEvent(ev); }}
                          >
                            <span className="font-medium">{format(rdvDate, 'HH:mm')}</span>
                            {' '}
                            {ev.is_own && ev.prospect ? `${ev.prospect.nom || ''} ${ev.prospect.prenom || ''}`.trim() : (ev.prospect?.ville || 'RDV')}
                          </div>
                        );
                      })}
                      {dayEvs.length > 3 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCurrentDate(day); setViewMode('day'); }}
                          className="text-[10px] text-blue-600 font-medium hover:underline pl-1"
                        >
                          +{dayEvs.length - 3} de plus
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // === Ouvrir Google Maps satellite ===
  const openGoogleMapsSatellite = (prospect) => {
    const lat = prospect?.latitude;
    const lng = prospect?.longitude;
    if (lat && lng && lat !== 0 && lng !== 0) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}&t=k&z=19`, '_blank');
    } else {
      const query = [prospect?.adresse, prospect?.code_postal, prospect?.ville].filter(Boolean).join(', ');
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank');
    }
  };

  // === EVENT DETAIL POPUP ===
  const EventDetailDialog = () => {
    if (!selectedEvent) return null;
    const ev = selectedEvent;
    const rdvDate = typeof ev.date_rdv === 'string' ? parseISO(ev.date_rdv) : new Date(ev.date_rdv);
    const color = getEventColor(ev);
    const prospect = ev.prospect;
    const isOwn = ev.is_own;
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'administratif' || currentUser?.role === 'confirmateur';
    const canSeeAll = isOwn || isAdmin;

    return (
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-sm ${color.bg}`} />
              <DialogTitle className="text-lg">
                {canSeeAll && prospect ? `${prospect.nom || ''} ${prospect.prenom || ''}`.trim() || 'Rendez-vous' : 'Rendez-vous'}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-3">
            {/* Date & Heure */}
            <div className="flex items-center gap-3 text-sm">
              <Clock className="w-4 h-4 text-gray-400 shrink-0" />
              <div>
                <p className="font-medium">{format(rdvDate, 'EEEE d MMMM yyyy', { locale: fr })}</p>
                <p className="text-gray-500">
                  {format(rdvDate, 'HH:mm')} – {format(addHours(rdvDate, (ev.duree_minutes || 60) / 60), 'HH:mm')}
                  {' '}({ev.duree_minutes || 60} min)
                </p>
              </div>
            </div>

            {/* Statut */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${color.bg} text-white text-xs`}>
                {STATUS_LABELS[ev.status] || ev.status}
              </Badge>
              {ev.resultat && <Badge variant="outline" className="text-xs">{RESULTAT_LABELS[ev.resultat] || ev.resultat}</Badge>}
              <Badge variant="outline" className="text-xs">{TYPE_RDV_LABELS[ev.type_rdv] || ev.type_rdv}</Badge>
            </div>

            {canSeeAll && prospect ? (
              <>
                {/* Adresse + Google Maps satellite */}
                {(prospect.adresse || prospect.ville) && (
                  <div className="flex items-start gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      {prospect.adresse && <p>{prospect.adresse}</p>}
                      <p className="text-gray-500">{prospect.code_postal} {prospect.ville}</p>
                      <button onClick={() => openGoogleMapsSatellite(prospect)}
                        className="inline-flex items-center gap-1.5 mt-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors">
                        <MapPin className="w-3 h-3" /> Vue satellite Google Maps
                      </button>
                    </div>
                  </div>
                )}
                {/* Téléphone */}
                {prospect.telephone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                    <a href={`tel:${prospect.telephone}`} className="text-blue-600 hover:underline">{prospect.telephone}</a>
                    {prospect.telephone_fixe && <span className="text-gray-400">/ {prospect.telephone_fixe}</span>}
                  </div>
                )}
                {/* Email */}
                {prospect.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <CalendarIcon className="w-4 h-4 text-gray-400 shrink-0" />
                    <a href={`mailto:${prospect.email}`} className="text-blue-600 hover:underline">{prospect.email}</a>
                  </div>
                )}
                {/* Entreprise / SIREN */}
                {(prospect.nom_entreprise || prospect.siren) && (
                  <div className="flex items-center gap-3 text-sm">
                    <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                    <span>{prospect.nom_entreprise}{prospect.siren && ` (SIREN: ${prospect.siren})`}</span>
                  </div>
                )}
                {/* Énergie */}
                {(prospect.type_energie || prospect.fournisseur_actuel || prospect.consommation_annuelle_euro) && (
                  <div className="flex items-start gap-3 text-sm">
                    <Zap className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                    <div>
                      {prospect.type_energie && <span className="capitalize">{prospect.type_energie}</span>}
                      {prospect.fournisseur_actuel && <span className="text-gray-500"> · {prospect.fournisseur_actuel}</span>}
                      {prospect.consommation_annuelle_euro > 0 && <p className="text-gray-500">{prospect.consommation_annuelle_euro}€/an</p>}
                      {prospect.consommation_annuelle_kwh > 0 && <span className="text-gray-500"> · {prospect.consommation_annuelle_kwh} kWh/an</span>}
                    </div>
                  </div>
                )}
                {/* Chauffage */}
                {prospect.mode_chauffage_actuel && (
                  <div className="flex items-center gap-3 text-sm">
                    <Zap className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-gray-600">Chauffage : {prospect.mode_chauffage_actuel}</span>
                  </div>
                )}
                {/* Infos personnelles — Questions/Réponses */}
                {(prospect.situation_familiale || prospect.domaine_activite || prospect.type_contrat_travail || prospect.montant_credit_en_cours) && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Situation personnelle</p>
                    {prospect.situation_familiale && <p className="text-sm text-gray-700">Situation : <span className="font-medium">{prospect.situation_familiale}</span></p>}
                    {prospect.domaine_activite && <p className="text-sm text-gray-700">Activité : <span className="font-medium">{prospect.domaine_activite}</span></p>}
                    {prospect.type_contrat_travail && <p className="text-sm text-gray-700">Contrat : <span className="font-medium">{prospect.type_contrat_travail}</span></p>}
                    {prospect.montant_credit_en_cours > 0 && <p className="text-sm text-gray-700">Crédit en cours : <span className="font-medium">{prospect.montant_credit_en_cours}€</span></p>}
                  </div>
                )}
                {/* Infos Monsieur/Madame */}
                {(prospect.type_contrat_monsieur || prospect.type_contrat_madame || prospect.revenus_mensuels_monsieur || prospect.revenus_mensuels_madame) && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Foyer</p>
                    {(prospect.type_contrat_monsieur || prospect.revenus_mensuels_monsieur) && (
                      <p className="text-sm text-gray-700">Monsieur : {prospect.type_contrat_monsieur && <span>{prospect.type_contrat_monsieur}</span>}{prospect.revenus_mensuels_monsieur > 0 && <span className="font-medium"> — {prospect.revenus_mensuels_monsieur}€/mois</span>}</p>
                    )}
                    {(prospect.type_contrat_madame || prospect.revenus_mensuels_madame) && (
                      <p className="text-sm text-gray-700">Madame : {prospect.type_contrat_madame && <span>{prospect.type_contrat_madame}</span>}{prospect.revenus_mensuels_madame > 0 && <span className="font-medium"> — {prospect.revenus_mensuels_madame}€/mois</span>}</p>
                    )}
                  </div>
                )}
                {/* Toiture & Qualification */}
                {(prospect.nombre_m2_toit || prospect.type_toiture || prospect.orientation_toit || prospect.velux_chien_assis || prospect.zone_classee) && (
                  <div className="bg-purple-50 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-purple-500 uppercase">Toiture</p>
                    {prospect.nombre_m2_toit > 0 && <p className="text-sm text-gray-700">Surface toit : <span className="font-medium">{prospect.nombre_m2_toit} m²</span></p>}
                    {prospect.type_toiture && <p className="text-sm text-gray-700">Type : <span className="font-medium">{prospect.type_toiture}</span></p>}
                    {prospect.orientation_toit && <p className="text-sm text-gray-700">Orientation : <span className="font-medium">{prospect.orientation_toit}</span></p>}
                    {prospect.velux_chien_assis && <p className="text-sm text-gray-700">Velux/Chien assis : <span className="font-medium">{prospect.velux_chien_assis}</span></p>}
                    {prospect.zone_classee && <p className="text-sm text-gray-700">Zone classée : <span className="font-medium">{prospect.zone_classee}</span></p>}
                  </div>
                )}
                {/* Âges, enfants, crédits */}
                {(prospect.nombre_enfants || prospect.age_monsieur || prospect.age_madame || prospect.montant_credit_immobilier || prospect.autre_credit) && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Complément</p>
                    {prospect.nombre_enfants > 0 && <p className="text-sm text-gray-700">Enfants à charge : <span className="font-medium">{prospect.nombre_enfants}</span></p>}
                    {prospect.age_monsieur > 0 && <p className="text-sm text-gray-700">Âge Monsieur : <span className="font-medium">{prospect.age_monsieur} ans</span></p>}
                    {prospect.age_madame > 0 && <p className="text-sm text-gray-700">Âge Madame : <span className="font-medium">{prospect.age_madame} ans</span></p>}
                    {prospect.montant_credit_immobilier > 0 && <p className="text-sm text-gray-700">Crédit immobilier : <span className="font-medium">{prospect.montant_credit_immobilier}€</span></p>}
                    {prospect.autre_credit && <p className="text-sm text-gray-700">Autre crédit : <span className="font-medium">{prospect.autre_credit}</span></p>}
                  </div>
                )}
                {/* Notes */}
                {prospect.notes && (
                  <div className="text-sm bg-blue-50 rounded-lg p-3 text-blue-700">
                    <span className="font-medium text-xs text-blue-500">Notes Télépro :</span><br/>{prospect.notes}
                  </div>
                )}
                {prospect.notes_commercial && (
                  <div className="text-sm bg-purple-50 rounded-lg p-3 text-purple-700">
                    <span className="font-medium text-xs text-purple-500">Notes Commercial :</span><br/>{prospect.notes_commercial}
                  </div>
                )}
                {/* Commentaire RDV */}
                {ev.commentaire_rdv && (
                  <div className="text-sm bg-yellow-50 rounded-lg p-3 text-yellow-800">
                    <span className="font-medium">Commentaire RDV :</span> {ev.commentaire_rdv}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-600">{prospect?.code_postal} {prospect?.ville || 'Non renseigné'}</p>
                  <p className="text-xs text-gray-400 italic mt-1">
                    <EyeOff className="w-3 h-3 inline mr-1" />
                    Informations masquées (RDV d'un autre télépro)
                  </p>
                </div>
              </div>
            )}

            {/* Créateur, Assigné & Date de création */}
            <div className="border-t pt-3 space-y-1">
              {ev.created_at && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3" /><span>Créé le : {(() => { try { const d = new Date(ev.created_at); return format(d, 'dd/MM/yyyy à HH:mm', { locale: fr }); } catch { return ''; } })()}</span>
                </div>
              )}
              {ev.created_by_name && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <User className="w-3 h-3" /><span>Créé par : {ev.created_by_name}</span>
                </div>
              )}
              {ev.assigned_to_name && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <User className="w-3 h-3" /><span>Assigné à : {ev.assigned_to_name}</span>
                </div>
              )}
              {prospect?.source_prospect && (
                <div className="flex items-center gap-2 text-xs text-indigo-600">
                  <Zap className="w-3 h-3" /><span>Source : {prospect.source_prospect}</span>
                </div>
              )}
            </div>

            {/* Actions — toujours visibles pour propriétaire et admin */}
            {canSeeAll && prospect && (
              <div className="border-t pt-3 space-y-2">
                {/* Email actions */}
                <div className="flex gap-2">
                  {prospect.email ? (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.post(`${API}/api/rendez-vous/${ev.id}/send-confirmation`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success(`Email de confirmation envoyé à ${prospect.email}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Erreur envoi email');
                          }
                        }}
                        className="flex-1 flex items-center justify-center gap-2 text-sm bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <CalendarIcon className="w-3.5 h-3.5" /> Envoyer confirmation
                      </button>
                      <a
                        href={`mailto:${prospect.email}?subject=${encodeURIComponent(`Confirmation RDV - ${format(rdvDate, 'dd/MM/yyyy HH:mm')}`)}&body=${encodeURIComponent(`Bonjour ${prospect.prenom || ''} ${prospect.nom || ''},\n\nNous vous confirmons votre rendez-vous le ${format(rdvDate, 'EEEE d MMMM yyyy', { locale: fr })} à ${format(rdvDate, 'HH:mm')}.\n\nAdresse : ${prospect.adresse || ''}, ${prospect.code_postal || ''} ${prospect.ville || ''}\n\nCordialement,\nSUNLIFE Solar`)}`}
                        className="flex items-center justify-center gap-2 text-sm border border-gray-200 text-gray-700 py-2 px-3 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <Phone className="w-3.5 h-3.5" /> Mailto
                      </a>
                    </>
                  ) : (
                    <div className="flex-1 text-center text-xs text-gray-400 py-2 bg-gray-50 rounded-md">
                      Pas d'email renseigné — ajoutez un email au prospect pour envoyer une confirmation
                    </div>
                  )}
                </div>
                {/* Bouton Modifier */}
                {onEditProspect && (
                  <button
                    onClick={() => { setSelectedEvent(null); onEditProspect(prospect); }}
                    className="w-full flex items-center justify-center gap-2 text-sm border border-gray-200 text-gray-700 py-2 px-3 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Modifier le prospect
                  </button>
                )}
                {onEditRdv && (
                  <button
                    onClick={() => { setSelectedEvent(null); onEditRdv(ev); }}
                    className="w-full flex items-center justify-center gap-2 text-sm bg-orange-500 text-white py-2 px-3 rounded-md hover:bg-orange-600 transition-colors"
                  >
                    <Clock className="w-3.5 h-3.5" /> Modifier / Assigner le RDV
                  </button>
                )}
                {onDeleteRdv && (
                  <button
                    onClick={() => {
                      if (window.confirm('Supprimer ce rendez-vous ?')) {
                        setSelectedEvent(null);
                        onDeleteRdv(ev);
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 text-sm bg-red-500 text-white py-2 px-3 rounded-md hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Supprimer le RDV
                  </button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // === LEGEND ===
  // Filtres cliquables
  const allFilters = [
    ...Object.entries(STATUS_COLORS).map(([key, val]) => ({ key, label: STATUS_LABELS[key], bg: val.bg })),
    { key: 'signe', label: 'Signé', bg: 'bg-green-500' },
    { key: 'r2', label: 'R2', bg: 'bg-orange-500' },
  ];

  const FilterLegend = ({ size = 'normal' }) => (
    <div className={`flex items-center flex-wrap ${size === 'small' ? 'gap-1.5' : 'gap-2'}`}>
      {allFilters.map(f => (
        <button key={f.key} onClick={() => setStatusFilter(statusFilter === f.key ? null : f.key)}
          className={`flex items-center gap-1 ${size === 'small' ? 'text-[10px]' : 'text-xs'} transition-all rounded-full px-1.5 py-0.5
            ${statusFilter === f.key ? 'ring-2 ring-offset-1 ring-gray-400 font-bold' : statusFilter && statusFilter !== f.key ? 'opacity-30' : ''}`}>
          <div className={`${size === 'small' ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-sm ${f.bg}`} />
          <span>{f.label}</span>
        </button>
      ))}
      {statusFilter && (
        <button onClick={() => setStatusFilter(null)} className={`${size === 'small' ? 'text-[10px]' : 'text-xs'} text-blue-600 hover:underline ml-1`}>
          Tout
        </button>
      )}
    </div>
  );

  // === RENDER ===
  return (
    <div ref={calendarRef} className={`flex flex-col bg-white overflow-hidden
      ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full rounded-lg border border-gray-200 shadow-sm'}`}>
      {/* Header — responsive Google Calendar style */}
      <div className="flex items-center justify-between px-2 md:px-4 py-2 border-b bg-white gap-1">
        <div className="flex items-center gap-1 md:gap-3 min-w-0">
          {/* Bouton plein écran — mobile only */}
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(!isFullscreen)} className="h-7 w-7 shrink-0">
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={goToday} className="text-[10px] md:text-xs font-medium h-7 px-2 md:px-3 shrink-0">
            {isMobile ? "Auj." : "Aujourd'hui"}
          </Button>
          <div className="flex items-center shrink-0">
            <Button variant="ghost" size="icon" onClick={goPrev} className="h-7 w-7">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={goNext} className="h-7 w-7">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <h2 className="text-sm md:text-base font-medium text-gray-900 capitalize truncate">
            {getHeaderTitle()}
          </h2>
          {/* Filtres cliquables */}
          <div className="hidden md:block ml-3">
            <FilterLegend size="small" />
          </div>
        </div>

        <div className="flex items-center gap-1 md:gap-2 shrink-0">
          {/* View switcher — adapté mobile */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {(isMobile ? [
              { key: 'day', label: 'J' },
              { key: '3days', label: '3J' },
              { key: 'month', label: 'M' },
            ] : [
              { key: 'day', label: 'Jour' },
              { key: 'week', label: 'Semaine' },
              { key: 'month', label: 'Mois' },
            ]).map(v => (
              <button
                key={v.key}
                onClick={() => setViewMode(v.key)}
                className={`px-2 md:px-3 py-1 md:py-1.5 text-[10px] md:text-xs font-medium rounded-md transition-colors
                  ${viewMode === v.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {/* Buttons */}
          <div className="flex items-center gap-1">
            {canViewReportes && (
              <Button size="sm" variant="outline" onClick={() => setShowReportesListing(true)}
                className="h-7 px-2 md:px-3 text-orange-600 border-orange-200 hover:bg-orange-50">
                <Eye className="w-3 h-3" />{!isMobile && <span className="ml-1">Reportés</span>}
              </Button>
            )}
            {canExport && (
              <Button size="sm" variant="outline" onClick={exportReportesXLSX}
                className="h-7 px-2 md:px-3 text-green-600 border-green-200 hover:bg-green-50">
                <Download className="w-3 h-3" />{!isMobile && <span className="ml-1">XLSX</span>}
              </Button>
            )}
            {canManageIndispo && (
              <Button size="sm" variant="outline" onClick={() => {
                const now = new Date();
                const def_start = format(setMinutes(setHours(now, 9), 0), "yyyy-MM-dd'T'HH:mm");
                const def_end = format(setMinutes(setHours(now, 18), 0), "yyyy-MM-dd'T'HH:mm");
                setIndispoForm({ date_debut: def_start, date_fin: def_end, motif: '', user_id: '' });
                setShowIndispoDialog(true);
              }} className="h-7 px-2 md:px-3 text-red-600 border-red-200 hover:bg-red-50">
                🚫{!isMobile && <span className="ml-1">Indispo</span>}
              </Button>
            )}
            <Button size="sm" onClick={() => handleSlotClick(new Date(), 9, 0)}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm h-7 px-2 md:px-3">
              <Plus className="w-4 h-4" />{!isMobile && <span className="ml-1">Nouveau</span>}
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar body */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Chargement...</p>
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'week' && <WeekView />}
          {viewMode === '3days' && <ThreeDaysView />}
          {viewMode === 'day' && <DayView />}
          {viewMode === 'month' && <MonthView />}
        </>
      )}

      {/* Dialogs */}
      <EventDetailDialog />

      {/* Dialog Indisponibilité */}
      <Dialog open={showIndispoDialog} onOpenChange={setShowIndispoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🚫 Ajouter une indisponibilité</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {canAssignIndispo && (
              <div>
                <Label className="text-xs">Assigner à (commercial)</Label>
                <select value={indispoForm.user_id} onChange={e => setIndispoForm({...indispoForm, user_id: e.target.value})}
                  className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm">
                  <option value="">Moi-même</option>
                  {events.reduce((users, ev) => {
                    if (ev.assigned_to_name && !users.find(u => u.id === ev.assigned_to)) users.push({ id: ev.assigned_to, name: ev.assigned_to_name });
                    return users;
                  }, []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Début</Label>
                <Input type="datetime-local" value={indispoForm.date_debut}
                  onChange={e => setIndispoForm({...indispoForm, date_debut: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Fin</Label>
                <Input type="datetime-local" value={indispoForm.date_fin}
                  onChange={e => setIndispoForm({...indispoForm, date_fin: e.target.value})} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Motif</Label>
              <Input value={indispoForm.motif} placeholder="Ex: Congé, Formation..."
                onChange={e => setIndispoForm({...indispoForm, motif: e.target.value})} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowIndispoDialog(false)}>Annuler</Button>
              <Button onClick={handleCreateIndispo} className="bg-red-500 hover:bg-red-600 text-white">
                🚫 Ajouter l'indisponibilité
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Listing Reportés */}
      <Dialog open={showReportesListing} onOpenChange={setShowReportesListing}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>RDV Reportés ({events.filter(ev => ev.status === 'reporte' && ev.is_own && ev.prospect).length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {events.filter(ev => ev.status === 'reporte' && ev.is_own && ev.prospect).length === 0 ? (
              <p className="text-center text-gray-400 py-8">Aucun RDV reporté</p>
            ) : (
              events.filter(ev => ev.status === 'reporte' && ev.is_own && ev.prospect).map(ev => {
                const p = ev.prospect;
                const d = ev.date_rdv ? new Date(ev.date_rdv) : null;
                return (
                  <div key={ev.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm">
                        <div><span className="text-gray-400">Date:</span> <strong>{d ? d.toLocaleDateString('fr-FR') : '-'} {d ? `${d.getHours()}h${String(d.getMinutes()).padStart(2,'0')}` : ''}</strong></div>
                        <div><span className="text-gray-400">Nom:</span> <strong>{p.prenom} {p.nom}</strong></div>
                        <div><span className="text-gray-400">Tél:</span> <a href={`tel:${p.telephone}`} className="text-blue-600">{p.telephone}</a></div>
                        <div><span className="text-gray-400">Email:</span> {p.email || '-'}</div>
                        <div className="col-span-2"><span className="text-gray-400">Adresse:</span> {p.adresse}, {p.code_postal} {p.ville}</div>
                        <div><span className="text-gray-400">Énergie:</span> {p.type_energie || '-'}</div>
                        <div><span className="text-gray-400">Fournisseur:</span> {p.fournisseur_actuel || '-'}</div>
                        <div><span className="text-gray-400">Chauffage:</span> {p.mode_chauffage_actuel || '-'}</div>
                        <div><span className="text-gray-400">Toiture:</span> {p.type_toiture || '-'} {p.orientation_toit || ''}</div>
                        <div><span className="text-gray-400">Surface toit:</span> {p.nombre_m2_toit ? `${p.nombre_m2_toit}m²` : '-'}</div>
                        <div><span className="text-gray-400">Zone classée:</span> {p.zone_classee || '-'}</div>
                        {p.notes && <div className="col-span-2 md:col-span-4 bg-gray-100 rounded p-2 text-xs text-gray-600">{p.notes}</div>}
                        {ev.commentaire_rdv && <div className="col-span-2 md:col-span-4 bg-yellow-50 rounded p-2 text-xs text-yellow-700">Commentaire: {ev.commentaire_rdv}</div>}
                      </div>
                      <div className="text-right text-xs text-gray-400 shrink-0">
                        <p>Par: {ev.created_by_name}</p>
                        {ev.assigned_to_name && <p>→ {ev.assigned_to_name}</p>}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProspectCalendar;
